import type {MealDrinkKind} from './meal-link-v1.ts';
export interface MainMealPolicyV1{
  readonly active:boolean;
  readonly eligibleProductIds:readonly string[];
  readonly eligibleCategoryIds?:readonly string[];
  readonly drinkPoolId:string;
  /** Legacy field retained for older snapshots; never selects a drink automatically. */
  readonly defaultDrinkChoiceId?:string;
  readonly checkoutReminder?:Readonly<{enabled:boolean;excludedChannels:readonly string[]}>;
  readonly platformDrinkRules?:Readonly<Record<string,Readonly<{removedKinds:readonly MealDrinkKind[];removedChoiceIds:readonly string[];retainedChoiceIds:readonly string[]}>>>;
}
export const DEFAULT_MAIN_MEAL_CATEGORY_IDS=Object.freeze(['legacy-mf01-category-02','legacy-mf01-category-03','legacy-mf01-category-07','legacy-mf01-category-12','legacy-mf01-category-13','legacy-mf01-category-14']);
export const DEFAULT_MEAL_REMINDER=Object.freeze({enabled:true,excludedChannels:['keeta','foodpanda']});
export const DEFAULT_PLATFORM_DRINK_RULE=Object.freeze({removedKinds:['HOT','COLD','NONE','PENDING'] as readonly MealDrinkKind[],removedChoiceIds:[] as readonly string[],retainedChoiceIds:[] as readonly string[]});
/**
 * Admin default (H1-4): no main-meal policy is "未設定" (undefined/null). Admin never generates one
 * from hard-coded category ids or pool ids. When the Owner chooses to create one, it starts empty
 * and inactive; every id is picked by hand in the editor.
 */
export const MAIN_MEAL_POLICY_UNSET=null;
export function emptyMainMealPolicy(drinkPoolId=''):MainMealPolicyV1{
  return {active:false,eligibleProductIds:[],eligibleCategoryIds:[],drinkPoolId,checkoutReminder:DEFAULT_MEAL_REMINDER};
}
/** Ids in a policy that no longer exist in the catalog, so the editor can show and remove them. */
export function mainMealPolicyMissingIds(policy:MainMealPolicyV1,productIds:ReadonlySet<string>,categoryIds:ReadonlySet<string>,drinkPoolIds:ReadonlySet<string>){
  return {
    productIds:policy.eligibleProductIds.filter(id=>!productIds.has(id)),
    categoryIds:(policy.eligibleCategoryIds??[]).filter(id=>!categoryIds.has(id)),
    drinkPoolId:policy.drinkPoolId&&!drinkPoolIds.has(policy.drinkPoolId)?policy.drinkPoolId:'',
  };
}
/**
 * @deprecated Legacy read-side fallback for snapshots published before main-meal policies existed
 * (worker customer projection and POS projection; tracked by MFK-MANUAL-OPS-HARDENING H3-1).
 * Admin must not use this to fill a missing policy.
 */
export function initialMainMealPolicy(products:readonly {id:string;categoryId:string;active:boolean}[]):MainMealPolicyV1{
  return {active:true,eligibleProductIds:products.filter(product=>product.active&&DEFAULT_MAIN_MEAL_CATEGORY_IDS.includes(product.categoryId)).map(product=>product.id),eligibleCategoryIds:[...DEFAULT_MAIN_MEAL_CATEGORY_IDS],drinkPoolId:'combo-drink-pool-shared',checkoutReminder:DEFAULT_MEAL_REMINDER,platformDrinkRules:{keeta:DEFAULT_PLATFORM_DRINK_RULE,foodpanda:DEFAULT_PLATFORM_DRINK_RULE}};
}
export function mealReminderEnabled(policy:MainMealPolicyV1|null,channel:string){
  const reminder=policy?.checkoutReminder??DEFAULT_MEAL_REMINDER;
  return Boolean(policy&&reminder.enabled&&!reminder.excludedChannels.includes(channel)&&!policy.platformDrinkRules?.[channel]);
}
export function validateMainMealPolicy(policy:MainMealPolicyV1,productIds:ReadonlySet<string>,categoryIds:ReadonlySet<string>,poolIds:ReadonlySet<string>,choiceIds:ReadonlySet<string>):string[]{
  const errors:string[]=[];
  if(typeof policy.active!=='boolean'||!poolIds.has(policy.drinkPoolId))errors.push('主食餐共用飲品Pool不存在');
  for(const id of policy.eligibleProductIds)if(!productIds.has(id))errors.push('主食餐產品不存在：'+id);
  for(const id of policy.eligibleCategoryIds??[])if(!categoryIds.has(id))errors.push('主食餐分類不存在：'+id);
  if(policy.checkoutReminder&&typeof policy.checkoutReminder.enabled!=='boolean')errors.push('結帳飲品提示設定無效');
  for(const [channel,rule] of Object.entries(policy.platformDrinkRules??{})){
    if(!channel.trim()||rule.removedKinds.some(kind=>!['HOT','COLD','SPECIAL','NONE','PENDING'].includes(kind)))errors.push('平台飲品類型設定無效');
    for(const id of [...rule.removedChoiceIds,...rule.retainedChoiceIds])if(!choiceIds.has(id))errors.push('平台飲品選項不存在：'+id);
    if(rule.removedChoiceIds.some(id=>rule.retainedChoiceIds.includes(id)))errors.push('平台飲品不能同時保留及移除');
  }
  return errors;
}
export interface PlatformMealComponentV1{readonly canonicalProductId:string;readonly quantity:number;readonly drinkChoiceId?:string}
export interface PlatformSmallMealV1{readonly id:string;readonly name:string;readonly providerOptionCode:string;readonly components:readonly PlatformMealComponentV1[]}
export interface PlatformPriceRowV1{readonly targetType:'PRODUCT'|'DRINK_CHOICE';readonly targetId:string;readonly keetaPrice:string;readonly foodpandaPrice:string}
export interface MealPlatformSettingsV1{
  readonly smallMeals:readonly PlatformSmallMealV1[];
  readonly prices:readonly PlatformPriceRowV1[];
  readonly estimateRates:Readonly<{keeta:number;foodpanda:number}>;
}
export const DEFAULT_PLATFORM_ESTIMATE_RATES=Object.freeze({keeta:75,foodpanda:75});
export const LEGACY_DRINK_BAND_KINDS:Readonly<Record<string,MealDrinkKind>>=Object.freeze({
  'drink-no-drink':'NONE','drink-hot-free':'HOT','drink-cold-plus-3':'COLD',
  'drink-special-plus-6':'SPECIAL','drink-special-plus-8':'SPECIAL','drink-special-plus-10':'SPECIAL',
});
export function normalizeMealPlatformSettings(input?:Partial<MealPlatformSettingsV1>):MealPlatformSettingsV1{
  return {smallMeals:input?.smallMeals??[],prices:input?.prices??[],estimateRates:{...DEFAULT_PLATFORM_ESTIMATE_RATES,...input?.estimateRates}};
}
export function validateMealPlatformSettings(settings:MealPlatformSettingsV1,productIds:ReadonlySet<string>,drinkChoiceIds:ReadonlySet<string>):string[]{
  const errors:string[]=[];
  for(const [channel,rate] of Object.entries(settings.estimateRates))if(!Number.isFinite(rate)||rate<0||rate>100)errors.push(channel+' 實收估算率必須為0至100');
  const optionCodes=new Set<string>();
  for(const meal of settings.smallMeals){
    if(!meal.id.trim()||!meal.name.trim()||!meal.providerOptionCode.trim())errors.push('平台小餐必須有名稱及Keeta選項ID');
    if(optionCodes.has(meal.providerOptionCode))errors.push('平台小餐Keeta選項ID重複：'+meal.providerOptionCode);
    optionCodes.add(meal.providerOptionCode);
    if(!meal.components.length)errors.push('平台小餐 '+meal.name+' 未設定組件');
    for(const component of meal.components){
      if(!productIds.has(component.canonicalProductId))errors.push('平台小餐 '+meal.name+' 指向不存在商品');
      if(!Number.isSafeInteger(component.quantity)||component.quantity<1)errors.push('平台小餐 '+meal.name+' 組件數量必須為正整數');
      if(component.drinkChoiceId&&!drinkChoiceIds.has(component.drinkChoiceId))errors.push('平台小餐 '+meal.name+' 飲品Pool選擇不存在');
    }
  }
  for(const row of settings.prices){
    if(!(row.targetType==='PRODUCT'?productIds:drinkChoiceIds).has(row.targetId))errors.push('平台價對應項目不存在：'+row.targetId);
    for(const value of [row.keetaPrice,row.foodpandaPrice])if(value.trim()&&(!Number.isFinite(Number(value))||Number(value)<0))errors.push('平台價必須為非負金額');
  }
  return errors;
}
export function platformSettlementReadback(storeTotalMinor:number,rate:number,providerEarningsMinor?:number){
  if(Number.isSafeInteger(providerEarningsMinor)&&Number(providerEarningsMinor)>=0)return {basis:'PROVIDER_REPORTED' as const,amountMinor:providerEarningsMinor!,label:'平台回報實收'};
  if(!Number.isSafeInteger(storeTotalMinor)||storeTotalMinor<0||!Number.isFinite(rate)||rate<0||rate>100)throw Error('PLATFORM_ESTIMATE_INPUT_INVALID');
  return {basis:'STORE_PRICE_ESTIMATE' as const,amountMinor:Math.round(storeTotalMinor*rate/10000)*100,rate,label:'估算實收'};
}
